/**
 * One icon family, drawn inline.
 *
 * A dependency would pull in hundreds of glyphs to use six, and mixing icon
 * styles is the fastest way to break cohesion. These are stroked, 24-grid, and
 * always paired with a text label — never the only signifier.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 11l8-6 8 6" />
      <path d="M6 10v9h12v-9" />
    </Icon>
  );
}

export function SortIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16M7 12h10M10 17h4" />
    </Icon>
  );
}

export function EnvelopeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3.5 7.5l8.5 6 8.5-6" />
    </Icon>
  );
}

export function BillIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" />
      <path d="M9.5 8.5h5M9.5 12.5h5" />
    </Icon>
  );
}

export function RulesIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6h10M4 12h7M4 18h13" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="15" cy="12" r="2" />
    </Icon>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2M12 19v2M4.5 7.5l1.7 1M17.8 15.5l1.7 1M4.5 16.5l1.7-1M17.8 8.5l1.7-1" />
    </Icon>
  );
}

export function ChatIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 6.5h9a2.5 2.5 0 0 1 2.5 2.5v4A2.5 2.5 0 0 1 14 15.5H9l-3.5 3v-3H5A2.5 2.5 0 0 1 2.5 13V9A2.5 2.5 0 0 1 5 6.5z" />
      <path d="M16 8.5h3A2.5 2.5 0 0 1 21.5 11v3.5A2.5 2.5 0 0 1 19 17h-.5v2L15.5 17" />
    </Icon>
  );
}

export function MapIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6.5l5-2.5 6 2.5 5-2.5v13l-5 2.5-6-2.5-5 2.5z" />
      <path d="M9 4v13M15 6.5v13" />
    </Icon>
  );
}

export function SplitIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12h5l3-5h8" />
      <path d="M12 17h8" />
      <path d="M17 4l3 3-3 3M17 14l3 3-3 3" />
    </Icon>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </Icon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 13l4 4L19 7" />
    </Icon>
  );
}

/** Pairs with warning text, so the meaning is never carried by color alone. */
export function WarningIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4" />
      <path d="M12 17.2v.1" />
    </Icon>
  );
}

export function PinIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 4h6l-1 7H10L9 4z" />
      <path d="M12 11v9" />
    </Icon>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="2.5" />
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5L20 20" />
    </Icon>
  );
}

export function BrandMark(props: IconProps) {
  // The same mountain silhouette as BrandMark in Theme.swift.
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M2.9 17.3L12 4.3l9.1 13H16.3L12 10.1l-4.3 7.2z" />
    </svg>
  );
}
